// src/features/developers/components/common.tsx
//
// Small pieces shared by the four tabs: a code box with its copy button, inline `code` inside a
// sentence, and a status pill. Colours, radii and borders come from the theme.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, Button, Typography, alpha, useTheme } from '@mui/material';
import { CheckRounded as CopiedIcon, ContentCopyRounded as CopyIcon } from '@mui/icons-material';

export const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/** `text` with backticked fragments drawn as inline code. */
export const InlineText: React.FC<{ text: string }> = ({ text }) => {
  const theme = useTheme();
  return (
    <>
      {text.split(/(`[^`]+`)/g).map((part, i) =>
        part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
          <Box
            key={i}
            component="code"
            sx={{
              fontFamily: MONO,
              fontSize: '0.92em',
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '5px',
              px: '5px',
              py: '1px',
              overflowWrap: 'anywhere',
            }}
          >
            {part.slice(1, -1)}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

/** Copies `text` and flips a label for a moment. */
export const useCopy = (): { copiedId: string | null; copy: (id: string, text: string) => void } => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = useCallback((id: string, text: string) => {
    void navigator.clipboard?.writeText(text);
    setCopiedId(id);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1600);
  }, []);
  return { copiedId, copy };
};

export const CodeBlock: React.FC<{ id: string; code: string; wrap?: boolean; copiedId: string | null; onCopy: (id: string, text: string) => void }> = ({
  id,
  code,
  wrap,
  copiedId,
  onCopy,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const copied = copiedId === id;
  return (
    <Box sx={{ position: 'relative', mt: 1.25, minWidth: 0 }}>
      <Button
        size="small"
        onClick={() => onCopy(id, code)}
        startIcon={copied ? <CopiedIcon sx={{ fontSize: 14 }} /> : <CopyIcon sx={{ fontSize: 14 }} />}
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 1,
          fontSize: '0.75rem',
          minHeight: 28,
          color: copied ? theme.palette.success.dark : theme.palette.text.secondary,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': { backgroundColor: theme.palette.background.paper },
        }}
      >
        {translate(copied ? 'developers.copied' : 'developers.copy')}
      </Button>
      <Typography
        component="pre"
        tabIndex={0}
        sx={{
          fontFamily: MONO,
          fontSize: '0.75rem',
          lineHeight: 1.6,
          m: 0,
          p: '44px 16px 14px',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.custom.radius.card}`,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          overflowX: 'auto',
          whiteSpace: wrap ? 'pre-wrap' : 'pre',
          wordBreak: wrap ? 'break-all' : 'normal',
        }}
      >
        {code}
      </Typography>
    </Box>
  );
};

export const Pill: React.FC<{ children: React.ReactNode; dot?: boolean }> = ({ children, dot }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '9999px',
        px: 1.25,
        py: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {dot && <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: alpha(theme.palette.success.main, 1) }} />}
      {children}
    </Box>
  );
};

/** The base address the samples and the environment list use, from the page's info. */
export const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

/** An environment's name for the reader's language: by its key, falling back to the API's own name. */
export const environmentName = (
  translate: (key: string, options?: Record<string, unknown>) => string,
  environment: { key: string; name: string },
): string => translate(`developers.environments.${environment.key}`, { _: environment.name });
